"use client";

import { startTransition, useMemo } from "react";
import { useAtom, useAtomValue } from "jotai";

import {
  DEFAULT_CHAT_MODEL,
  chatThinkingEfforts,
  getAvailableChatThinkingEfforts,
  isSelectedChatModel,
  isSelectedChatThinkingEffort,
  resolveChatThinkingEffort,
  supportsChatModelThinkingEffort,
} from "@tracify/shared";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
} from "@tracify/ui/select";
import { chatModalModelAtom, chatThinkingEffortAtom } from "@/atoms";
import { cn } from "@/lib/utils";

const effortByValue = new Map(
  chatThinkingEfforts.map((effort) => [effort.value, effort])
);

export function ThinkingEffortSelector({ className }: { className?: string }) {
  const chatModel = useAtomValue(chatModalModelAtom);
  const [desiredEffort, setDesiredEffort] = useAtom(chatThinkingEffortAtom);
  const selectedModel = isSelectedChatModel(chatModel)
    ? chatModel
    : DEFAULT_CHAT_MODEL;
  const availableEfforts = useMemo(
    () => getAvailableChatThinkingEfforts(selectedModel),
    [selectedModel]
  );
  const effectiveEffort = resolveChatThinkingEffort(
    selectedModel,
    desiredEffort
  );
  const selectedEffort = effortByValue.get(effectiveEffort);
  const isDisabled = !supportsChatModelThinkingEffort(selectedModel);

  return (
    <Select
      value={effectiveEffort}
      disabled={isDisabled}
      onValueChange={(value) => {
        if (
          !isSelectedChatThinkingEffort(value) ||
          !availableEfforts.includes(value)
        ) {
          return;
        }

        startTransition(() => {
          setDesiredEffort(value);
        });
      }}
    >
      <SelectTrigger
        id="thinking-effort-selector"
        data-testid="thinking-effort-selector"
        className={cn(
          "w-fit border-none hover:bg-muted disabled:opacity-60",
          className
        )}
        size="sm"
        showIcon={false}
      >
        {selectedEffort?.label ?? "Default"}
      </SelectTrigger>
      <SelectContent className="min-w-[220px]">
        <SelectGroup className="space-y-1">
          {availableEfforts.map((effortValue) => {
            const effort = effortByValue.get(effortValue);
            if (!effort) {
              return null;
            }

            return (
              <SelectItem
                key={effort.value}
                value={effort.value}
                data-testid={`thinking-effort-selector-item-${effort.value}`}
              >
                <div className="text-sm font-medium leading-tight">
                  {effort.label}
                </div>
                <div className="mt-1 text-xs font-normal leading-none text-muted-foreground">
                  {effort.description}
                </div>
              </SelectItem>
            );
          })}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}
